"use client";

import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import { useSession } from "next-auth/react";
import { useEffect, useState } from "react";
import AnalyticsPageStats from "~/components/analytics/AnalyticsPageStats";
import { adminLyricsService } from "~/connectors/lyrics.connector";
import type { AdminTopSearchQuery, LyricsPageStats } from "~/types";

interface Props {
  slug: string;
}

export default function LyricsPageStats({ slug }: Props) {
  const { data: session } = useSession();
  const isAdmin = session?.user?.role === "admin";

  const [stats, setStats] = useState<LyricsPageStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [topSearches, setTopSearches] = useState<AdminTopSearchQuery[]>([]);

  // Period start (last 30 days). keeps stable reference for tooltips and requests.
  const [periodStart] = useState(() => new Date(Date.now() - 30 * 24 * 60 * 60 * 1000));

  useEffect(() => {
    if (!isAdmin) return;

    setTimeout(() => setLoading(true), 0);
    adminLyricsService
      .getPageStats({ slug, startAt: periodStart })
      .then((data) => setStats(data))
      .catch(() => setStats(null))
      .finally(() => setLoading(false));
  }, [isAdmin, slug, periodStart]);

  useEffect(() => {
    if (!isAdmin) return;

    adminLyricsService
      .getTopSearchQueries({ slug, cachedOnly: true })
      .then((rows) => setTopSearches(rows))
      .catch(() => setTopSearches([]));
  }, [isAdmin, slug]);

  if (!isAdmin) return null;

  if (loading) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
        <CircularProgress size={14} />
        <Typography variant="caption" sx={{
          color: "text.secondary"
        }}>
          Se încarcă statisticile...
        </Typography>
      </Box>
    );
  }

  if (!stats) return null;

  return (
    <Box>
      <AnalyticsPageStats stats={stats} periodStart={periodStart} topSearches={topSearches} />
    </Box>
  );
}
